import { Fragment, useCallback, useContext } from 'react';
import { COMPANY_TYPE_ID, CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { _tracker } from 'customer-data-tracking/tracker';
// @ts-expect-error Could not find a declaration file for module
import { trackSpecificPropertyEvent } from 'customer-data-sidebar/tracking/events';
import PropertiesFormDependentSection from 'customer-data-properties/form/PropertiesFormDependentSection';
import CommunicationLink from '../../components/CommunicationLink';
import RecentPropertyChangeWarning from '../../components/RecentPropertyChangeWarning';
import PropertyHistoryButton from '../../components/PropertyHistoryButton';
import { getPropertyLabel } from 'customer-data-property-utils/PropertyDisplay';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { HIDDEN, NOT_SPECIFIED } from 'customer-data-objects/property/AccessLevel';
import { AccessLevelContext, getAccessLevelWithCaching } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { isReadOnly } from 'customer-data-objects/property/PropertyIdentifier';
import http from 'hub-http/clients/apiClient';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import SidebarPropertyError from '../../components/SidebarPropertyError';
import { isBidenValue } from 'customer-data-objects/model/PropertyValueModel';
import { List } from 'foundations-components/transitional/components/List';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { getPropertyValue } from 'customer-data-objects/model/ImmutableModel';
import { BOT, CONVERSATIONS, FORM } from 'customer-data-objects/property/PropertySourceTypes';
import { usePropertyInputV2Actions } from '../hooks/usePropertyInputV2Actions';
import { usePropertiesCardContext } from './PropertiesCardContext';
import styled from 'styled-components';
import { Flex } from 'foundations-components/components/Flex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { isHardcodedReadOnly } from '../../utils/isHardcodedReadOnly';
import { BlockIcon } from 'foundations-assets/icons/components/BlockIcon';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const RECENT_PROPERTY_CHANGE_WARNING_MIN_AGE = 300000; // 5 min in ms
const RECENT_PROPERTY_CHANGE_WARNING_THRESHOLD = 86400000; // 24 hrs in ms
const RECENT_PROPERTY_CHANGE_WARNING_SOURCES = [BOT, CONVERSATIONS, FORM];
const StyledDiv = styled.div.withConfig({
  displayName: "PropertiesCardInputs__StyledDiv",
  componentId: "sc-9y6jbu-0"
})(["word-break:normal;"]);
const PropertiesCardInputs = props => {
  const {
    inputs,
    subject,
    allProperties
  } = props;
  const {
    isEmbedded,
    onShowPropertyHistory,
    isEditable,
    objectTypeId,
    objectId,
    shouldHideDetailsButton
  } = usePropertiesCardContext();
  const accessLevelContext = useContext(AccessLevelContext);
  const handleTracking = useCallback(action => {
    trackSpecificPropertyEvent(_tracker, {
      action
    });
  }, []);
  const checkIsHardcodedReadOnly = useCallback(inputProperty => {
    return isHardcodedReadOnly(inputProperty, objectTypeId, isEmbedded);
  }, [isEmbedded, objectTypeId]);

  // See original requirements https://git.hubteam.com/HubSpot/customer-data-sidebar/pull/596
  const renderRecentPropertyChangeWarning = property => {
    // Only show for contact email
    if (objectTypeId !== CONTACT_TYPE_ID || property.name !== 'email') {
      return null;
    }

    // Only if property exists
    const propertyValue = getPropertyValue(subject, property.name);
    if (!propertyValue) {
      return null;
    }
    const {
      value,
      source,
      timestamp
    } = propertyValue;
    const createDate = getPropertyValue(subject, 'createdate');
    if (
    // Only if property has been set
    !source || !timestamp ||
    // Only if set from an abusable source
    !RECENT_PROPERTY_CHANGE_WARNING_SOURCES.includes(source) ||
    // Only if set recently
    Date.now() - timestamp > RECENT_PROPERTY_CHANGE_WARNING_THRESHOLD ||
    // Only if set after contact was initially created (e.g., don't show a
    // warning for brand new contacts created from forms).
    // NOTE: include a fudge factor; property timestamps aren't
    //       guaranteed to match createdates for new objects
    createDate && createDate.value && timestamp - parseInt(createDate.value, 10) <= RECENT_PROPERTY_CHANGE_WARNING_MIN_AGE) {
      return null;
    }

    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
    return /*#__PURE__*/_jsx(RecentPropertyChangeWarning, {
      value: value,
      source: source
    });
  };
  const actions = usePropertyInputV2Actions(subject);
  const getPassthroughProps = inputProperty => {
    const accessLevel = getAccessLevelWithCaching({
      context: accessLevelContext,
      objectType: objectTypeId,
      propertyName: inputProperty.name,
      httpClient: http
    });
    const hasPermissionToEdit = accessLevel === null || accessLevel === NOT_SPECIFIED;
    const readOnly = !isEditable || checkIsHardcodedReadOnly(inputProperty) || isReadOnly(inputProperty) || !hasPermissionToEdit;
    // This placeholder is a short-term solution to prevent the floating form
    // inputs from animating on hover when the form is read-only
    // https://hubspot.slack.com/archives/C05CVAZDMME/p1691002918684349?thread_ts=1690912836.279019&cid=C05CVAZDMME
    const placeholder = readOnly ? '--' : undefined;
    const shouldRenderInsightsLabel = () => {
      if (objectTypeId !== COMPANY_TYPE_ID) {
        return false;
      }
      const property = allProperties.get(inputProperty.name);
      if (property && Object.prototype.hasOwnProperty.call(property, 'source')) {
        // @ts-expect-error isBidenValue argument has incorrect type
        return isBidenValue(property);
      }
      return false;
    };
    const renderLabel = () => {
      const label = getPropertyLabel(inputProperty);
      const shouldShowInsightsTooltip = shouldRenderInsightsLabel();
      const shouldShowHiddenTooltip = accessLevel === HIDDEN;
      const shouldShowPermissionsTooltip = !shouldShowHiddenTooltip && !hasPermissionToEdit;
      if (!shouldShowInsightsTooltip && !shouldShowPermissionsTooltip && !shouldShowHiddenTooltip) {
        return label;
      }
      return /*#__PURE__*/_jsxs(Flex, {
        as: "span",
        alignItems: "center",
        gap: "100",
        children: [/*#__PURE__*/_jsx(Tooltip, {
          disabled: !shouldShowHiddenTooltip,
          title: label ? /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardProperties.permissions.hiddenWithLabel",
            options: {
              propertyLabel: label
            }
          }) : /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardProperties.permissions.hidden"
          }),
          children: /*#__PURE__*/_jsx("span", {
            children: label
          })
        }), shouldShowInsightsTooltip && /*#__PURE__*/_jsx(Tooltip, {
          title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: "cardProperties.insightsTooltip"
          }),
          children: /*#__PURE__*/_jsx(InfoIcon, {
            color: "icon-core-subtle"
          })
        }), shouldShowPermissionsTooltip && /*#__PURE__*/_jsx(Tooltip, {
          placement: "top",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "cardProperties.permissions.READ_ONLY",
            "data-selenium-test": `edit-restricted-tooltip-${inputProperty.name}`
          }),
          children: /*#__PURE__*/_jsx(BlockIcon, {
            color: "icon-core-subtle"
          })
        })]
      });
    };
    const property = allProperties.get(inputProperty.name);
    const propertyValue = property ? property.value : undefined;
    return {
      use: 'floating',
      readOnly,
      enableMultipleEmailInput: true,
      actions,
      onTracking: handleTracking,
      inputProps: {
        placeholder,
        showPlaceholder: !!placeholder,
        'data-selenium-test': `property-input-${inputProperty.name}`
      },
      formControlProps: Object.assign({
        label: renderLabel()
      }, shouldHideDetailsButton ? {
        hoverContent: null
      } : {}, {
        overlayContent: /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(CommunicationLink, {
            objectTypeId: objectTypeId,
            name: inputProperty.name,
            value: propertyValue
          }), onShowPropertyHistory !== null && /*#__PURE__*/_jsx(PropertyHistoryButton, {
            property: inputProperty,
            objectType: objectTypeId,
            objectId: objectId,
            onShowPropertyHistory: ({
              property: propertySrc
            }) => onShowPropertyHistory({
              objectTypeId,
              objectId,
              property: propertySrc
            })
          })]
        })
      })
    };
  };
  const renderDependents = (dependents, path) => {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: dependents.map(dependent => {
        return /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "FloatingForm_SidebarDependentPropertyError",
            ErrorComponent: SidebarPropertyError,
            errorProps: {
              propertyLabel: getPropertyLabel(dependent.property)
            },
            children: /*#__PURE__*/_jsx("span", {
              "data-test-id": dependent.property.name,
              children: dependent.render(getPassthroughProps(dependent.property))
            })
          }), renderDependents(dependent.dependents, `${path}-${dependent.property.name}`)]
        }, `${path}-${dependent.property.name}`);
      })
    });
  };
  return /*#__PURE__*/_jsx(StyledDiv, {
    "data-selenium-test": "profile-properties",
    children: /*#__PURE__*/_jsx(List, {
      "data-selenium-test": "profile-properties-list",
      children: inputs.map(({
        render,
        dependents,
        property
      }) => /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(ErrorBoundary, {
          boundaryName: "FloatingForm_SidebarPropertyError",
          ErrorComponent: SidebarPropertyError,
          errorProps: {
            propertyLabel: getPropertyLabel(property)
          },
          children: /*#__PURE__*/_jsxs("span", {
            "data-test-id": property.name,
            children: [render(getPassthroughProps(property)), renderRecentPropertyChangeWarning(property)]
          })
        }), dependents.length > 0 && /*#__PURE__*/_jsx(PropertiesFormDependentSection, {
          property: property,
          children: renderDependents(dependents, property.name)
        })]
      }, property.name))
    })
  });
};
export default PropertiesCardInputs;